// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Alert Management Hook
 * Manages alert notifications for user feedback
 * Uses Zustand for global state management so alerts can be triggered from anywhere
 */

import { create } from 'zustand';

export interface Alert {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

interface AlertStore {
  alerts: Alert[];
  addAlert: (alert: Alert) => void;
  removeAlert: (id: string) => void;
  clearAll: () => void;
}

// Create global store
const useAlertStore = create<AlertStore>((set) => ({
  alerts: [],
  addAlert: (alert) => set((state) => ({ alerts: [...state.alerts, alert] })),
  removeAlert: (id) => set((state) => ({ alerts: state.alerts.filter((a) => a.id !== id) })),
  clearAll: () => set({ alerts: [] }),
}));

export function useAlerts() {
  const { alerts, addAlert, removeAlert, clearAll } = useAlertStore();

  const showAlert = (
    type: Alert['type'],
    message: string,
    title?: string,
    autoClose: boolean = true,
    autoCloseDelay: number = 5000
  ) => {
    const id = `alert-${Date.now()}-${Math.random()}`;
    const newAlert: Alert = {
      id,
      type,
      message,
      title,
      autoClose,
      autoCloseDelay,
    };

    addAlert(newAlert);
    return id;
  };

  const showSuccess = (message: string, title?: string) => {
    return showAlert('success', message, title);
  };

  const showError = (message: string, title?: string) => {
    return showAlert('error', message, title, true, 8000); // Longer for errors
  };

  const showWarning = (message: string, title?: string) => {
    return showAlert('warning', message, title);
  };

  const showInfo = (message: string, title?: string) => {
    return showAlert('info', message, title);
  };

  return {
    alerts,
    showAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeAlert: removeAlert,
    clearAll,
  };
}
